import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { fmtDateShort, fmtTimeRange } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HoursInput } from "@/components/attendance/HoursInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, ClipboardCheck, CalendarClock, MoreHorizontal, Eye, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TRACK_SHORT, courseYears, sessionYearsLabel } from "@/lib/school";
import { YearLevelsPicker } from "@/components/attendance/YearLevelsPicker";
import { SessionStudentsPicker } from "@/components/attendance/SessionStudentsPicker";

export default function Attendance() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteSession, setDeleteSession] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toLocaleDateString("en-CA"),
    start_time: new Date().toTimeString().slice(0, 5),
    course_id: "",
    title: "",
    course_track: "",
    year_levels: [],
    track_type: "",
    notes: "",
    duration_hours: 1,
    prepopulate_active_students: true,
    exclude_student_ids: [],
    extra_student_ids: [],
  });

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["attendance-sessions"],
    queryFn: async () => (await api.get("/attendance/sessions")).data,
  });
  const { data: overview } = useQuery({
    queryKey: ["attendance-overview"],
    queryFn: async () => (await api.get("/attendance/overview")).data,
  });
  const { data: courses = [] } = useQuery({
    queryKey: ["courses-all"],
    queryFn: async () => (await api.get("/courses")).data,
    enabled: createOpen,
  });

  const selectedCourse = courses.find((c) => c.id === form.course_id);
  const pickerYears = form.year_levels.length ? form.year_levels
    : (selectedCourse?.year_levels?.length ? selectedCourse.year_levels.map(Number) : selectedCourse?.year_level ? [Number(selectedCourse.year_level)] : []);

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (!payload.track_type) payload.track_type = null;
      if (!payload.title) delete payload.title; // deriva dal corso
      return (await api.post("/attendance/sessions", payload)).data;
    },
    onSuccess: (s) => {
      toast.success("Sessione creata");
      setCreateOpen(false);
      setForm({ ...form, title: "", notes: "", course_id: "", exclude_student_ids: [], extra_student_ids: [] });
      qc.invalidateQueries({ queryKey: ["attendance-sessions"] });
      qc.invalidateQueries({ queryKey: ["attendance-overview"] });
      navigate(`/scuola/presenze/${s.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/attendance/sessions/${id}`)).data,
    onSuccess: () => {
      toast.success("Sessione eliminata");
      qc.invalidateQueries({ queryKey: ["attendance-sessions"] });
      qc.invalidateQueries({ queryKey: ["attendance-overview"] });
      setDeleteSession(null);
    },
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Presenze"
        subtitle="Sessioni giornaliere con registro presenze, assenze, ritardi e giustificazioni."
        actions={
          <Button
            data-testid="attendance-add-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150"
          >
            <Plus size={16} className="mr-1.5"/> Nuova sessione
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><ClipboardCheck size={12}/> Sessioni totali</div>
          <div className="text-2xl font-semibold mt-1">{overview?.total_sessions || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><CalendarClock size={12}/> Programmate</div>
          <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{overview?.upcoming_sessions || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><TrendingUp size={12}/> Tasso presenza</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-700">{(overview?.attendance_rate || 0).toFixed(0)}%</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Assenze registrate</div>
          <div className="text-2xl font-semibold mt-1 text-red-700">{overview?.counts?.assente || 0}</div>
        </Card>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 text-sm font-medium">Sessioni recenti</div>
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
              <TableHead className="uppercase text-[10px] tracking-widest">Data e orario</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Titolo</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Percorso</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Riepilogo</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest text-right">Tasso</TableHead>
              <TableHead className="w-12"/>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7} className="text-center py-10 text-stone-400">Caricamento…</TableCell></TableRow>}
            {!isLoading && sessions.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-stone-400">
                Nessuna sessione. Crea la prima con &quot;Nuova sessione&quot;.
              </TableCell></TableRow>
            )}
            {sessions.map((s, i) => (
              <TableRow key={s.id} data-testid={`session-row-${s.id}`} className="animate-in-row hover:bg-stone-50/50" style={{ animationDelay: `${i*8}ms` }}>
                <TableCell className="text-sm whitespace-nowrap">
                  <div className="font-medium" data-testid={`session-date-${s.id}`}>{fmtDateShort(s.date)}</div>
                  {s.start_time && <div className="text-[11px] text-stone-500" data-testid={`session-time-${s.id}`}>{fmtTimeRange(s.start_time, s.end_time)}</div>}
                </TableCell>
                <TableCell>
                  <Link to={`/scuola/presenze/${s.id}`} className="font-medium hover:underline">{s.title}</Link>
                  {s.course_name ? (
                    <div className="mt-0.5">
                      <Badge className={`text-[10px] rounded-full ${s.is_seminar ? "bg-[#E2725B]/10 text-[#8f3e26] border-[#E2725B]/30" : "bg-emerald-50 text-emerald-800 border-emerald-200"} border`}>
                        {s.is_seminar ? "SEMINARIO" : "CORSO"} · {s.course_name}
                      </Badge>
                    </div>
                  ) : (
                    <div className="mt-0.5">
                      <Badge className="text-[10px] rounded-full bg-amber-50 text-amber-800 border border-amber-200">Sessione generica</Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {sessionYearsLabel(s) ? (
                    <Badge data-testid={`session-years-${s.id}`} className="bg-[#1C352D] text-white rounded-full text-[10px] px-2 py-0.5">{sessionYearsLabel(s)}</Badge>
                  ) : <span className="text-stone-400 text-xs">Tutte</span>}
                </TableCell>
                <TableCell className="text-xs text-stone-600">
                  {s.track_type ? <Badge className="bg-stone-100 border border-stone-200 text-stone-700 rounded-full text-[10px]">{TRACK_SHORT[s.track_type]}</Badge> : <span className="text-stone-400">—</span>}
                  {s.course_track && <div className="text-[10px] text-stone-500 mt-0.5">{s.course_track}</div>}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1.5 flex-wrap">
                    <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full text-[10px]">P {s.counts?.presente || 0}</Badge>
                    <Badge className="bg-red-50 text-red-800 border border-red-200 rounded-full text-[10px]">A {s.counts?.assente || 0}</Badge>
                    <Badge className="bg-orange-50 text-orange-800 border border-orange-200 rounded-full text-[10px]">R {s.counts?.ritardo || 0}</Badge>
                    <Badge className="bg-stone-100 text-stone-700 border border-stone-200 rounded-full text-[10px]">G {s.counts?.giustificato || 0}</Badge>
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold">{(s.attendance_rate || 0).toFixed(0)}%</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`session-actions-${s.id}`} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D] transition-colors duration-150">
                        <MoreHorizontal size={16}/>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40 bg-white">
                      <DropdownMenuItem onClick={() => navigate(`/scuola/presenze/${s.id}`)}>
                        <Eye size={14} className="mr-2"/> Apri registro
                      </DropdownMenuItem>
                      <DropdownMenuSeparator/>
                      <DropdownMenuItem className="text-red-700 focus:text-red-800 focus:bg-red-50" onClick={() => setDeleteSession(s)}>
                        <Trash2 size={14} className="mr-2"/> Elimina
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] p-0 flex flex-col gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-stone-100">
            <DialogTitle>Nuova sessione di presenza</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div>
              <Label>Corso o Seminario <span className="text-red-600">*</span></Label>
              <Select value={form.course_id || "__none__"} onValueChange={(v) => {
                if (v === "__none__") { setForm({ ...form, course_id: "" }); return; }
                const c = courses.find((x) => x.id === v);
                setForm({ ...form, course_id: v, year_levels: courseYears(c), track_type: c?.track_type || "", duration_hours: c?.is_seminar && c?.hours ? c.hours : 1 });
              }}>
                <SelectTrigger data-testid="session-course" className="mt-1.5"><SelectValue placeholder="Seleziona un corso o seminario"/></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— Seleziona —</SelectItem>
                  {courses.filter((c) => c.status !== "archiviato").map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.is_seminar ? "🎓 " : ""}{c.name}{c.year_level ? ` · ${c.year_level}° anno` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCourse && (
                <div className="text-[11px] text-stone-500 mt-1.5">
                  {selectedCourse.is_seminar ? "Seminario" : "Corso"}
                  {selectedCourse.cfu != null && ` · ${selectedCourse.cfu} CFU`}
                  {selectedCourse.area_name && ` · ${selectedCourse.area_name}`}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label>Data</Label>
                <Input data-testid="session-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5"/>
              </div>
              <div>
                <Label>Ora inizio</Label>
                <Input data-testid="session-start-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1.5"/>
              </div>
              <div>
                <Label>Durata (ore)</Label>
                <HoursInput testId="session-duration" value={form.duration_hours} onChange={(n) => setForm({ ...form, duration_hours: n })} className="mt-1.5"/>
              </div>
            </div>
            <div>
              <Label>Studenti di quali anni?</Label>
              <div className="mt-1.5">
                <YearLevelsPicker value={form.year_levels} onChange={(v) => setForm({ ...form, year_levels: v })}/>
              </div>
            </div>
            <div>
              <Label>Titolo lezione <span className="text-stone-400 text-xs">(opz. — se vuoto usa il nome del corso)</span></Label>
              <Input data-testid="session-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={selectedCourse ? `${selectedCourse.name} — ${form.date}` : "Es. Teologia sistematica - Modulo 3"} className="mt-1.5"/>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5"/>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
              Le presenze in questa fase riguardano solo studenti <strong>residenziali</strong>. Gli studenti modulari non verranno inclusi nel registro.
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                data-testid="session-prepopulate"
                checked={form.prepopulate_active_students}
                onCheckedChange={(v) => setForm({ ...form, prepopulate_active_students: !!v })}
              />
              Pre-popola il registro con gli studenti residenziali attivi come &quot;Assenti&quot; (diventano presenti con il check-in QR; oltre la tolleranza risultano in ritardo)
            </label>
            {form.course_id && (
              <SessionStudentsPicker
                years={form.prepopulate_active_students ? pickerYears : [-1]}
                exclude={form.exclude_student_ids}
                extra={form.extra_student_ids}
                onChange={({ exclude, extra }) => setForm((f) => ({ ...f, exclude_student_ids: exclude, extra_student_ids: extra }))}
              />
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-stone-100 bg-white shrink-0">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button data-testid="session-save-btn" disabled={createMut.isPending || !form.course_id || !(form.duration_hours > 0 && form.duration_hours <= 24)} onClick={() => createMut.mutate()} className="bg-[#1C352D] hover:bg-[#264A3E]">
              Crea &amp; apri registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteSession} onOpenChange={(v) => { if (!v) setDeleteSession(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la sessione?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno rimosse anche tutte le registrazioni di presenza collegate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => deleteSession && delMut.mutate(deleteSession.id)}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
